// The plan grid's arithmetic — what a task is budgeted to cost and expected
// to earn, per cost group. Pure functions on plain objects: no DOM, no
// network, no Supabase (the cost-topics.js discipline).
//
// A "split" is one jsonb column's value: { material: 15000, c_scaffold: 500 }.
// Keys are topic keys; values are dollars. Junk values are ignored, never
// summed as NaN — a half-typed cell must not poison a project total.

import { toCents } from './task-costs.js';

/** Sum of one split's finite values. */
export function groupSplitTotal(split) {
  let sum = 0;
  Object.values(split || {}).forEach(v => {
    const n = Number(v);
    if (Number.isFinite(n)) sum += n;
  });
  return sum;
}

/**
 * The project's per-group budget as the sum over its tasks' plans.
 * `hasAny` is the switch the Job Costs tab flips on: true the moment ANY
 * task carries a planned dollar, at which point these totals replace
 * job_budgets as the budget source (spec: one source of truth, sum wins).
 * An unknown/archived group key folds into 'other' — same degrade rule as
 * every other read in the topics system: money is regrouped, never dropped.
 *
 * A stored 0 or negative is treated as absent. A 0 is reachable by a direct
 * API write that bypasses the UI (jvSetPlanCell in 68-jobvalue.js never
 * writes one), and a negative can only arrive the same way. Allowing either
 * would flip hasAny to true for a project with, in effect, nothing planned,
 * and a negative would poison a budget bar.
 */
export function projectBudgetFromTasks(lines, validKeys) {
  const valid = validKeys instanceof Set ? validKeys
    : (validKeys ? new Set(validKeys) : null);
  const totals = {};
  let hasAny = false;
  (lines || []).forEach(l => {
    const split = l && l.budget_by_group;
    if (!split || typeof split !== 'object') return;
    Object.entries(split).forEach(([k, v]) => {
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) return;
      hasAny = true;
      const key = valid && !valid.has(k) ? 'other' : k;
      totals[key] = (totals[key] || 0) + n;
    });
  });
  return { hasAny, totals };
}

// ── What the plan MEANS — derived, never stored (2026-10-02) ─────────────────
// Four readings of the same typed budget, each one a function of facts that
// already exist: the org's overhead %, each line's contract and earned value
// (job-value.js projectValue's perLine), each task's cost (task-costs.js).
// The screens only print what these return.

const _cents = toCents;
const _num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const _name = (s) => String(s ?? '').trim().toLowerCase();

/** Two group names that print the same word — trimmed, case-blind. */
export function sameName(a, b) {
  return _name(a) !== '' && _name(a) === _name(b);
}

/**
 * Overhead's budget, derived the way overhead itself is: one org-wide % of the
 * planned direct cost. The total is the anchor and overhead is the difference
 * — the engine's own rule (jcComputeProject: totalSpent, then overheadSpent) —
 * so direct + overhead = total at the cent. With no % set (or nothing
 * planned) overhead is 0 and total IS direct: nothing extra to print.
 */
export function overheadBudget(directBudget, overheadPct) {
  const pct = _num(overheadPct), raw = _num(directBudget);
  const direct = _cents(raw);
  if (!(pct > 0) || !(raw > 0)) return { direct, overhead: 0, total: direct };
  const total = _cents(raw * (1 + pct));
  return { direct, overhead: _cents(total - direct), total };
}

/**
 * How much of the job the plan covers. "The plan wins" the moment one line
 * carries a planned dollar (projectBudgetFromTasks.hasAny) — and from then on
 * the gauges compare the WHOLE job's cost with whatever was planned. That is
 * only a fair reading while every line that carries money has a plan.
 *
 * A line carries money when it is worth something (contract > 0), has earned
 * something (an open line has no contract by design) or has cost filed under
 * it. A credit — a line with a negative amount and no cost — cannot be given a
 * plan (the editor refuses a negative budget), so it never counts as missing.
 *
 *   planned    lines carrying a plan
 *   total      planned + the lines carrying money with no plan
 *   unplanned  those lines, in the order given
 *   partial    there IS a plan and it leaves at least one of them out
 */
export function planCoverage(lines, valueByLineId, costByLineId, validKeys) {
  const values = valueByLineId || {}, costs = costByLineId || {};
  let planned = 0;
  const unplanned = [];
  (lines || []).forEach(l => {
    if (!l) return;
    if (projectBudgetFromTasks([l], validKeys).hasAny) { planned++; return; }
    const v = values[l.id] || {};
    if (_num(v.contract) > 0 || _num(v.earned) > 0 || _num(costs[l.id]) > 0.005) unplanned.push(l);
  });
  return { planned, total: planned + unplanned.length, unplanned,
           partial: planned > 0 && unplanned.length > 0 };
}

/**
 * What the plan allows for the work done SO FAR, per group — the budget's
 * pace. Per line, not per job: a group's allowance is the sum over the
 * planned lines of that line's budget × that line's own earned ÷ contract.
 * (One job-wide % would hand the labour budget of a line nobody has started
 * to the line that is nearly done.)
 *
 * null — no pace to read — when the job has an open line (no agreed total, so
 * "% earned" means nothing), when a planned line has no contract value, or
 * when nothing has been earned yet (an allowance of $0 is not a pace).
 *
 * A FINISHED line (finished_on set, owner 2026-10-05) counts as fully earned
 * whatever its percent says — frac 1, its budget judged whole — so neither an
 * open shape nor a missing contract value stops the pace for it.
 *
 *   byGroup     { topicKey: dollars allowed so far }, whole cents
 *   fracByLine  { lineId: 0-1 } for the planned lines
 *   frac        the ONE earned fraction every planned line shares (they print
 *               the same percent to a decimal), or null when they differ. A
 *               group's allowance is then a blend of several lines at
 *               different percents, and "at Y% earned" beside it would be a
 *               figure that is on no line and nowhere else on the screen.
 */
export function planPace(lines, valueByLineId, validKeys) {
  const values = valueByLineId || {};
  const raw = {}, fracByLine = {};
  let earned = 0, finished = false;
  for (const l of lines || []) {
    if (!l) continue;
    const v = values[l.id] || {};
    const done = !!l.finished_on;
    if (v.open && !done) return null;
    const plan = projectBudgetFromTasks([l], validKeys);
    if (!plan.hasAny) continue;
    const contract = _num(v.contract);
    if (!done && !(contract > 0)) return null;
    const frac = done ? 1 : Math.min(Math.max(_num(v.earned) / contract, 0), 1);
    fracByLine[l.id] = frac;
    earned += _num(v.earned);
    finished = finished || done;
    Object.entries(plan.totals).forEach(([k, n]) => { raw[k] = (raw[k] || 0) + n * frac; });
  }
  if (!(earned > 0) && !finished) return null;
  const byGroup = {};
  Object.keys(raw).forEach(k => { byGroup[k] = _cents(raw[k]); });
  const fracs = Object.values(fracByLine);
  const frac = new Set(fracs.map(f => Math.round(f * 1000))).size === 1 ? fracs[0] : null;
  return { byGroup, fracByLine, frac };
}

/**
 * The same pace for a job budgeted in one piece (job_budgets, no per-line
 * plan): there is only the job's own % earned to go by. `perLine` is
 * projectValue's; `unpricedCount` its unpriced lines — with one of those the
 * contract total is incomplete and the % is not a fact. Same nulls as above.
 */
export function jobPace(budgetByGroup, perLine, unpricedCount) {
  const list = perLine || [];
  if (unpricedCount > 0 || list.some(v => v && v.open)) return null;
  const contract = list.reduce((s, v) => s + _num(v && v.contract), 0);
  const earned = list.reduce((s, v) => s + _num(v && v.earned), 0);
  if (!(contract > 0) || !(earned > 0)) return null;
  const frac = Math.min(Math.max(earned / contract, 0), 1);
  const byGroup = {};
  Object.entries(budgetByGroup || {}).forEach(([k, n]) => {
    if (_num(n) > 0) byGroup[k] = _cents(_num(n) * frac);
  });
  return { byGroup, frac };
}

/**
 * One group against its budget AND its pace.
 *   tone      'over'  spent more than the whole budget        (red)
 *             'near'  inside the budget, ahead of the pace    (amber)
 *             'ok'    at or under the pace                    (green)
 *   variance  spent − allowance, whole cents: positive is over the pace
 *   share     allowance ÷ budget — the "% earned" the allowance stands for
 */
export function paceRead(spent, budget, allowance) {
  const s = _num(spent), b = _num(budget), a = _cents(allowance);
  const variance = _cents(s - a);
  return { tone: s > b ? 'over' : variance > 0 ? 'near' : 'ok',
           variance, share: b > 0 ? a / b : null };
}

// A typed figure in whole cents, or null when none is typed (the column is
// NULL). 0 is a typed answer; a negative never reaches here (the columns'
// CHECKs refuse it).
function _typedMoney(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? _cents(n) : null;
}
// A typed fraction (planned_profit_pct: 0.05 = 5%, like overhead_pct), or null.
function _typedPct(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * The plan priced the way the owner's estimate sheet prices a job, in this
 * order (owner, 2026-10-05 and 2026-10-15):
 *   direct      the typed cost groups — escalation is NOT one of them
 *   overhead    the org % of DIRECT (overheadBudget)
 *   cost        direct + overhead
 *   profit      ON the cost: planned_profit_pct × cost when a % is set (it
 *               follows the plan as it changes), else the typed amount
 *               (planned_profit); null when neither is typed
 *   escalation  planned_escalation, last: no overhead and no profit on it;
 *               0 when none is typed, and 0 on a task with no cost group
 *               typed — an allowance for cost increases needs a cost planned,
 *               and Job Costs (By task, coverage, planned margin) reads such a
 *               task as "no plan", so it is priced nowhere rather than in some
 *               readers and not others
 *   price       cost + profit + escalation; null when no profit is typed
 * `plan` is the contract line (any object carrying those three columns).
 *   profitPct   the % the profit is of cost (the typed one, or amount ÷ cost),
 *               null with no profit or no cost
 *   budget      cost + escalation — what the task is planned to COST: the
 *               escalation is an allowance for cost increases, so every
 *               budget-vs-actual reading (under/over budget, pace, planned
 *               margin, projected profit) is against this figure
 * Profit and escalation are columns of their own, never keys of
 * budget_by_group, so neither reaches direct cost, the overhead base, a gauge
 * or a group's pace.
 */
export function planPrice(directBudget, overheadPct, plan) {
  const p = plan || {};
  const oh = overheadBudget(directBudget, overheadPct);
  const pct = _typedPct(p.planned_profit_pct);
  const profit = pct != null ? _cents(oh.total * pct) : _typedMoney(p.planned_profit);
  const escalation = _num(directBudget) > 0 ? _typedMoney(p.planned_escalation) || 0 : 0;
  return { direct: oh.direct, overhead: oh.overhead, cost: oh.total, profit,
           profitPct: pct != null ? pct : profit != null && oh.total > 0 ? profit / oh.total : null,
           escalation, budget: _cents(oh.total + escalation),
           price: profit == null ? null : _cents(oh.total + profit + escalation) };
}

/** The escalation allowance across these lines, whole cents — as planPrice
 *  counts it: only on a line whose plan carries a cost group (`validKeys` as
 *  for projectBudgetFromTasks). */
export function escalationTotal(lines, validKeys) {
  return _cents((lines || []).reduce((s, l) => s + (l
    ? planPrice(groupSplitTotal(projectBudgetFromTasks([l], validKeys).totals), 0, l).escalation : 0), 0));
}

/**
 * Planned margin: what the contract leaves once the plan is spent — contract
 * minus the budget WITH its derived overhead and its escalation allowance
 * (planPrice's `budget`), so it is the same kind of figure as the actual
 * margin beside it (profit after overhead). A contract equal to the plan's
 * price leaves exactly the planned profit.
 *
 * A line with NO contract value (an open or unpriced line: the caller passes
 * 0) and a planned profit (typed, or a %): the planned margin IS that profit
 * (`contract` null in the answer). Otherwise null — a credit (a negative
 * contract: a profit on it is not a margin), no contract and no profit, or
 * nothing planned: "100% planned margin" on an unplanned line is not a finding.
 * `plan` is the contract line, as for planPrice.
 */
export function plannedMargin(contract, directBudget, overheadPct, plan) {
  if (!(_num(directBudget) > 0)) return null;
  const priced = planPrice(directBudget, overheadPct, plan);
  const c = _cents(contract);
  const { budget, escalation } = priced;
  if (c > 0) {
    const margin = _cents(c - budget);
    return { contract: c, budget, escalation, margin, pct: margin / c };
  }
  if (c < 0) return null;
  return priced.profit == null ? null : { contract: null, budget, escalation, margin: priced.profit, pct: null };
}

/**
 * A finished task's real result, and what moved it (owner, 2026-10-05: "if I
 * finish the task, the remainder must go in as profit").
 *
 * A line WITH a contract value (`contract` > 0 — the caller passes 0 for an
 * open or unpriced line) earns its whole contract once finished
 * (job-value.js projectValue), so its result is contract − cost: its planned
 * margin plus what was left of its budget, nothing not earned. Taken from the
 * contract rather than `earned` so a finish date still ahead forecasts the same.
 *
 * A line with NO contract value earns what it earned, as before, and against
 * its planned margin it moved by two things, said separately:
 *   saved  budget − cost, both with overhead (the budget with its escalation
 *          allowance too) — what was left of the budget
 *   gap    its planned price (cost + profit + escalation) − what it earned.
 *          Positive = earned short of it; negative = earned beyond it.
 * `cost` and `budget` carry overhead, `budget` its escalation too (planPrice's
 * `budget`; 0 = no plan); `margin` is
 * plannedMargin's, or null. Whole cents:
 *   result  contract − cost, or earned − cost; null with neither (no sell price)
 *   saved   null with no plan
 *   gap     0 on a contract line, with nothing it was to earn, or no earned figure
 * With a margin and a result, result − margin = saved − gap, to the cent.
 */
export function finishedResult(contract, earned, cost, budget, margin) {
  const c = _cents(contract), k = _cents(cost), b = _cents(budget);
  const e = c > 0 ? c : earned == null ? null : _cents(earned);
  const toEarn = c > 0 ? c : margin != null ? _cents(margin + b) : null;
  return {
    result: e == null ? null : _cents(e - k),
    saved: b > 0 ? _cents(b - k) : null,
    gap: toEarn == null || e == null ? 0 : _cents(toEarn - e),
  };
}

/**
 * Projected profit at completion (owner, 2026-10-05) — what the job is on
 * course to make, task by task. A finished task counts at its real result; a
 * running one at its planned margin.
 *
 * `rows` are task-costs.js taskTable rows { line, earned, cost }: whole cents,
 * cost WITH its overhead — the By-task row exactly as printed (earned null for
 * a line with no sell price). `contractById` is each line's contract where
 * there is one to subtract from (0 / absent for an open or unpriced line).
 * `generalCost` is taskTable's General: cost, with its overhead, filed under
 * no task.
 *
 * Per line:
 *   finished        its real result (finishedResult): contract − cost on a
 *                   line with a contract value, else earned − cost — none
 *                   while it has neither
 *   planned margin  the margin less any overrun already spent:
 *                   margin − max(0, cost − budget incl. overhead and escalation)
 *   neither         no projection — in `missing` when it carries money
 *
 * General is in no task's result. While a task is still running that cost
 * may yet be filed under it and land inside its budget, so it is only named
 * (`general`); once every task is finished there is nowhere left for it to
 * go, and it comes off the figure (`generalIn`). Every task finished, the
 * projection IS the job's profit, earned − every cost.
 *
 * The job: null when no line has a planned margin (no plan, no projected
 * figure — never "$0"). Otherwise, in whole cents,
 *   amount   = planned + saved − gap + unplanned − overrun − generalIn
 *              (re-adds on a calculator; finished = saved − gap + unplanned)
 *   planned    Σ planned margins
 *   saved      Σ over finished lines with a planned margin of budget − cost
 *   gap        Σ over the same lines of planned price − earned (only a line
 *              with no contract value can carry one)
 *   finishedPlanned    how many lines those are
 *   unplanned  Σ real results of finished lines with no planned margin
 *   finishedUnplanned  how many lines those are
 *   overrun    Σ over unfinished planned lines of what they spent past budget
 *   byLine     { id: { finished, amount, margin, overrun } } for lines with one
 *   missing    the lines carrying money with no projection, in the order given
 */
export function projectedProfit(rows, contractById, overheadPct, validKeys, generalCost) {
  const contracts = contractById || {};
  const byLine = {}, missing = [];
  let planned = 0, saved = 0, gap = 0, unplanned = 0, overrun = 0;
  let finishedPlanned = 0, finishedUnplanned = 0, running = 0, any = false;
  for (const r of rows || []) {
    const l = r && r.line;
    if (!l) continue;
    const direct = groupSplitTotal(projectBudgetFromTasks([l], validKeys).totals);
    const pm = plannedMargin(contracts[l.id], direct, overheadPct, l);
    const cost = _cents(r.cost);
    const margin = pm ? pm.margin : null;
    if (pm) { any = true; planned += margin; }
    const fin = l.finished_on
      ? finishedResult(contracts[l.id], r.earned, cost, direct > 0 ? planPrice(direct, overheadPct, l).budget : 0, margin)
      : null;
    if (fin && fin.result != null) {
      if (pm) { finishedPlanned++; saved += fin.saved; gap += fin.gap; }
      else { finishedUnplanned++; unplanned += fin.result; }
      byLine[l.id] = { finished: true, amount: fin.result, margin, overrun: 0 };
    } else if (pm) {
      running++;
      const over = Math.max(0, _cents(cost - pm.budget));
      overrun += over;
      byLine[l.id] = { finished: false, amount: _cents(margin - over), margin, overrun: over };
    } else if (_cents(contracts[l.id]) !== 0 || _num(r.earned) !== 0 || cost > 0) {
      missing.push(l);
    }
  }
  if (!any) return null;
  [planned, saved, gap, unplanned, overrun] = [planned, saved, gap, unplanned, overrun].map(_cents);
  const general = _cents(generalCost);
  const generalIn = running === 0 && missing.length === 0 ? general : 0;
  return { amount: _cents(planned + saved - gap + unplanned - overrun - generalIn), planned,
           finished: _cents(saved - gap + unplanned), saved, gap, unplanned, finishedPlanned, finishedUnplanned,
           overrun, general, generalIn, byLine, missing };
}

// ── Over the takeoff — the Job Costs first screen (2026-11-03) ───────────────
// Spec: docs/superpowers/specs/2026-11-03-job-costs-simple-view-design.md §6
// and §13. A reading, never a new number: the plan (budget_by_group, dollars)
// against the direct cost the roll already filed under each line
// (taskRoll.cells, dollars). Compared in whole cents; returned in dollars at
// the cent, like every other figure in this file.

// The reconciler's own labels (contract-line-lock.js reconcilePlan), shortened.
function _coLabel(l) {
  const d = String(l.description || '').trim();
  const m = d.match(/^(change order|extra work)\s+(.+)$/i);
  if (!m) return d;
  return (m[1].toLowerCase() === 'change order' ? 'CO ' : 'EWO ') + m[2];
}

/**
 * The tasks as the owner reads them: a base line together with every change
 * order that CONTINUES it (continues_line_id, walked to its root), in the
 * order the lines are given (contract order). A continuation whose base is
 * not in `lines` — or a looped chain a hand-edited row could make — is its
 * own unit. key = baseId = the unit's first line.
 */
export function takeoffUnits(lines) {
  const list = (lines || []).filter(l => l && l.id != null);
  const byId = Object.fromEntries(list.map(l => [l.id, l]));
  const root = (l) => {
    let r = l;
    const seen = new Set();
    while (r.continues_line_id && byId[r.continues_line_id] && !seen.has(r.id)) {
      seen.add(r.id);
      r = byId[r.continues_line_id];
    }
    return r.continues_line_id && byId[r.continues_line_id] ? l : r;   // a loop: stand alone
  };
  const units = {}, out = [];
  list.forEach(l => {
    if (root(l) !== l) return;
    units[l.id] = { key: l.id, baseId: l.id, lineIds: [l.id], coLabels: [] };
    out.push(units[l.id]);
  });
  list.forEach(l => {
    const r = root(l);
    if (r === l) return;
    units[r.id].lineIds.push(l.id);
    units[r.id].coLabels.push(_coLabel(l));
  });
  return out;
}

/**
 * How much of each line is built, 0-1, or null when it cannot be told.
 *   finished (finished_on set)          1
 *   a continuation (a change order)     its own progress: valueByLineId[id]
 *                                       .percent (projectValue — the overflow
 *                                       it absorbed); its typed
 *                                       percent_complete means nothing there
 *   a quantity line with a takeoff qty  installed ÷ estimated_qty, capped at 1,
 *                                       sell rate or not
 *   an open line (no takeoff qty)       null
 *   a percent line                      its typed percent
 * `installedByLine` is the quantity each base line absorbed
 * (allocateInstalled's byLineId); `valueByLineId` is projectValue's perLine
 * by id (Job Costs: d.jvPerLineById).
 */
export function fracByLine(lines, installedByLine, valueByLineId) {
  const inst = installedByLine || {}, vals = valueByLineId || {};
  const clamp = (f) => Math.min(Math.max(f, 0), 1);
  const out = {};
  (lines || []).forEach(l => {
    if (!l) return;
    if (l.finished_on) { out[l.id] = 1; return; }
    if (l.continues_line_id) {
      const p = vals[l.id] ? vals[l.id].percent : null;
      out[l.id] = p == null || !Number.isFinite(Number(p)) ? null : clamp(Number(p) / 100);
      return;
    }
    if (l.earn_method === 'quantity') {
      const est = _num(l.estimated_qty);
      out[l.id] = est > 0 ? clamp(_num(inst[l.id]) / est) : null;
      return;
    }
    out[l.id] = clamp(_num(l.percent_complete) / 100);
  });
  return out;
}

/** Groups that arrive before the work is built: read against the WHOLE takeoff
 *  (takeoffRead, and Job Costs' gauges). */
export const TAKEOFF_WHOLE_GROUPS = Object.freeze(['material', 'trucking']);

/**
 * One unit (takeoffUnits) against its takeoff, per cost group.
 *   allows   `wholeGroups` (material, trucking): the whole takeoff — they
 *            arrive before the work is built. Every other group: Σ over the
 *            unit's lines of plan × that line's own fraction built; a line
 *            whose fraction is unknown uses its whole plan, and so does every
 *            line while nothing at all is built yet (a week of hours before
 *            the first production report is not an alarm).
 *   spent, allows  in whole dollars, the figures the screen prints;
 *   result   spent − allows of those printed figures, so the row subtracts on
 *            a calculator; over at $1 or more
 *   notInTakeoff  spend on a group the takeoff has no amount for
 * Groups: over first, biggest first; the rest in the takeoff's order.
 *   state     'noPlan' | 'over' | 'noQty' | 'noProduction' | 'ok' — 'over'
 *             wins over the two after it: past the WHOLE takeoff is over
 *             whatever is built (spec §6.3, §6.4)
 *   progress  'finished' | 'built' | 'noQty' (no line's fraction known) |
 *             'noProduction' (nothing installed, nothing finished)
 *   pctBuilt  the unit's built %, weighted by each line's labour-type plan
 *             (its whole plan when it has none; the plain mean when no known
 *             line has any plan dollars); null when no line's fraction is known
 *   overTotal Σ of the reported overs; `escalation` the planned_escalation
 *             allowance (escalationTotal) — a note, never a group
 * `validKeys` as for projectBudgetFromTasks: a group deleted since the plan
 * was typed reads as Other, where the roll files its cost.
 */
export function takeoffRead(unit, opts) {
  const o = opts || {};
  const whole = new Set(o.wholeGroups || TAKEOFF_WHOLE_GROUPS);
  const lines = ((unit && unit.lineIds) || []).map(id => (o.lineById || {})[id]).filter(Boolean);
  const fracs = o.fracByLine || {}, cells = o.cellsByLine || {};
  const plans = lines.map(l => projectBudgetFromTasks([l], o.validKeys).totals);
  const hasPlan = plans.some(p => Object.keys(p).length > 0);
  const known = lines.filter(l => fracs[l.id] != null);
  const progress = lines.length > 0 && lines.every(l => l.finished_on) ? 'finished'
    : known.length === 0 ? 'noQty'
    : known.every(l => !(fracs[l.id] > 0)) ? 'noProduction' : 'built';
  const judgedWhole = progress === 'noQty' || progress === 'noProduction';
  const share = (l) => judgedWhole || fracs[l.id] == null ? 1 : fracs[l.id];
  // Whole dollars, read from whole cents so a float tail cannot tip a half.
  const W = (v) => (Math.sign(v) * Math.round(Math.round(Math.abs(v) * 100) / 100)) || 0;

  const keys = [];
  const add = (k) => { if (!keys.includes(k)) keys.push(k); };
  plans.forEach(p => Object.keys(p).forEach(add));
  lines.forEach(l => Object.keys(cells[l.id] || {}).forEach(add));
  const groups = [];
  keys.forEach(key => {
    let plan = 0, allows = 0, spent = 0;
    lines.forEach((l, i) => {
      const p = _num(plans[i][key]);
      plan += p;
      allows += whole.has(key) ? p : p * share(l);
      spent += _num((cells[l.id] || {})[key]);
    });
    if (!(plan > 0) && Math.abs(spent) < 0.005) return;
    const sw = W(spent), aw = W(allows);
    groups.push({ key, spent: sw, allows: aw, result: sw - aw,
      basis: whole.has(key) || judgedWhole ? 'whole' : 'built', notInTakeoff: !(plan > 0) });
  });
  const isOver = (x) => hasPlan && x.result >= 1;
  groups.sort((a, b) => (isOver(b) - isOver(a)) || (isOver(a) ? b.result - a.result : 0));
  const overs = groups.filter(isOver);

  let w = 0, wf = 0;
  known.forEach(l => {
    const t = plans[lines.indexOf(l)];
    const lab = Object.entries(t).reduce((s, [k, v]) => s + (whole.has(k) ? 0 : v), 0);
    const weight = lab > 0 ? lab : groupSplitTotal(t);
    w += weight; wf += weight * fracs[l.id];
  });

  return {
    state: !hasPlan ? 'noPlan' : overs.length ? 'over' : judgedWhole ? progress : 'ok',
    progress,
    // No known line has plan dollars (a change order with no takeoff): the plain mean.
    pctBuilt: w > 0 ? Math.round(wf / w * 100)
      : known.length ? Math.round(known.reduce((s, l) => s + fracs[l.id], 0) / known.length * 100) : null,
    groups,
    overTotal: overs.reduce((s, x) => s + x.result, 0),
    escalation: escalationTotal(lines, o.validKeys),
  };
}
